import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const isImageUrl = (url) => {
    if (!url) return false;
    const lower = url.split('?')[0].toLowerCase();
    return /\.(png|jpe?g|gif|bmp|webp|svg|tiff?)$/.test(lower);
};

const isPdfUrl = (url) => {
    if (!url) return false;
    const lower = url.split('?')[0].toLowerCase();
    return lower.includes('.pdf');
};

// ─── Component ───────────────────────────────────────────────────────────────
const OperationalReviewModal = ({
    isOpen,
    onClose,
    title = 'Revisión Documental',
    subtitle,
    accentColor = '#00897B',
    documentUrl,
    documentName,
    metaContent,
    children,
    saving = false,
    loading = false,
}) => {
    const handleBackdropClick = () => {
        if (!saving) onClose?.();
    };

    const handleCloseClick = () => {
        if (!saving) onClose?.();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div
                    onClick={handleBackdropClick}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)',
                        padding: '1rem',
                    }}
                >
                    <motion.div
                        onClick={(e) => e.stopPropagation()}
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 10 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        style={{
                            backgroundColor: '#ffffff', borderRadius: '1rem',
                            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                            width: '95%', maxWidth: '80rem', display: 'flex', flexDirection: 'column',
                            height: '90vh', overflow: 'hidden',
                        }}
                    >
                        {/* ── Header ── */}
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            borderBottom: '1px solid #e2e8f0', padding: '1rem 1.5rem',
                            borderTop: `3px solid ${accentColor}`,
                        }}>
                            <div>
                                <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#0f172a', margin: '0 0 0.25rem 0' }}>
                                    {title}
                                </h2>
                                {subtitle && (
                                    <p style={{ fontSize: '0.875rem', color: '#64748b', margin: 0 }}>
                                        {subtitle}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={handleCloseClick}
                                disabled={saving}
                                style={{
                                    color: saving ? '#cbd5e1' : '#94a3b8',
                                    backgroundColor: 'transparent', border: 'none',
                                    borderRadius: '9999px', padding: '0.5rem',
                                    cursor: saving ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                                onMouseOver={(e) => {
                                    if (!saving) {
                                        e.currentTarget.style.backgroundColor = '#f1f5f9';
                                        e.currentTarget.style.color = '#475569';
                                    }
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = saving ? '#cbd5e1' : '#94a3b8';
                                }}
                            >
                                <i className="pi pi-times" style={{ fontSize: '1.25rem' }} />
                            </button>
                        </div>

                        {/* ── Split Body ── */}
                        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                            {/* Left: Document Preview */}
                            <div style={{
                                width: '50%', borderRight: '1px solid #e2e8f0', backgroundColor: '#e2e8f0',
                                position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                {documentUrl ? (
                                    isPdfUrl(documentUrl) ? (
                                        <iframe
                                            src={`${documentUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                                            width="100%" height="100%"
                                            style={{ border: 'none', minHeight: '100%' }}
                                            title={documentName || 'Documento'}
                                        />
                                    ) : isImageUrl(documentUrl) ? (
                                        <img
                                            src={documentUrl}
                                            alt={documentName || 'Documento'}
                                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                        />
                                    ) : (
                                        /* Fallback: try iframe for unknown extensions (signed S3 URLs may not have ext) */
                                        <iframe
                                            src={documentUrl}
                                            width="100%" height="100%"
                                            style={{ border: 'none', minHeight: '100%' }}
                                            title={documentName || 'Documento'}
                                        />
                                    )
                                ) : (
                                    <div style={{
                                        color: '#64748b', display: 'flex', flexDirection: 'column',
                                        alignItems: 'center', gap: '1rem',
                                    }}>
                                        <i className="pi pi-file" style={{ fontSize: '3rem' }} />
                                        <span>No hay vista previa disponible</span>
                                    </div>
                                )}
                            </div>

                            {/* Right: Content */}
                            <div style={{
                                padding: '1.5rem', overflowY: 'auto', width: '50%',
                                backgroundColor: '#f8fafc',
                            }}>
                                {loading ? (
                                    /* ── Loading skeleton ── */
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        <style>{`@keyframes modalSkel{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
                                        {/* Meta placeholder */}
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            {[80, 100, 60].map((w, i) => (
                                                <div key={i} style={{
                                                    width: `${w}px`, height: '22px', borderRadius: '11px',
                                                    background: 'linear-gradient(90deg, #e2e8f0 25%, #cbd5e1 50%, #e2e8f0 75%)',
                                                    backgroundSize: '200% 100%', animation: 'modalSkel 1.4s infinite',
                                                }} />
                                            ))}
                                        </div>
                                        {/* Section header placeholder */}
                                        <div style={{
                                            width: '40%', height: '14px', borderRadius: '4px',
                                            background: 'linear-gradient(90deg, #e2e8f0 25%, #cbd5e1 50%, #e2e8f0 75%)',
                                            backgroundSize: '200% 100%', animation: 'modalSkel 1.4s infinite',
                                        }} />
                                        {/* Field rows */}
                                        {[1, 2, 3, 4, 5, 6].map(i => (
                                            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                                <div style={{
                                                    width: `${30 + Math.random() * 20}%`, height: '10px', borderRadius: '3px',
                                                    background: 'linear-gradient(90deg, #e2e8f0 25%, #cbd5e1 50%, #e2e8f0 75%)',
                                                    backgroundSize: '200% 100%', animation: 'modalSkel 1.4s infinite',
                                                }} />
                                                <div style={{
                                                    width: '100%', height: '32px', borderRadius: '6px',
                                                    background: 'linear-gradient(90deg, #e2e8f0 25%, #cbd5e1 50%, #e2e8f0 75%)',
                                                    backgroundSize: '200% 100%', animation: 'modalSkel 1.4s infinite',
                                                }} />
                                            </div>
                                        ))}
                                        {/* Reason + buttons placeholder */}
                                        <div style={{ marginTop: '0.5rem' }}>
                                            <div style={{
                                                width: '25%', height: '10px', borderRadius: '3px', marginBottom: '0.35rem',
                                                background: 'linear-gradient(90deg, #e2e8f0 25%, #cbd5e1 50%, #e2e8f0 75%)',
                                                backgroundSize: '200% 100%', animation: 'modalSkel 1.4s infinite',
                                            }} />
                                            <div style={{
                                                width: '100%', height: '32px', borderRadius: '6px',
                                                background: 'linear-gradient(90deg, #e2e8f0 25%, #cbd5e1 50%, #e2e8f0 75%)',
                                                backgroundSize: '200% 100%', animation: 'modalSkel 1.4s infinite',
                                            }} />
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
                                            <div style={{
                                                width: '80px', height: '32px', borderRadius: '6px',
                                                background: 'linear-gradient(90deg, #e2e8f0 25%, #cbd5e1 50%, #e2e8f0 75%)',
                                                backgroundSize: '200% 100%', animation: 'modalSkel 1.4s infinite',
                                            }} />
                                            <div style={{
                                                width: '160px', height: '32px', borderRadius: '6px',
                                                background: `linear-gradient(90deg, ${accentColor}30 25%, ${accentColor}50 50%, ${accentColor}30 75%)`,
                                                backgroundSize: '200% 100%', animation: 'modalSkel 1.4s infinite',
                                            }} />
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {/* Meta content (badges, info) */}
                                        {metaContent && (
                                            <div style={{ marginBottom: '1rem' }}>
                                                {metaContent}
                                            </div>
                                        )}

                                        {/* Form content (children) */}
                                        {children}
                                    </>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default OperationalReviewModal;
